import * as Log from 'next/dist/build/output/log';
import { cache } from "react";

/*
 * I'm sure you've noticed that all of the functions in this file seem to take a string representation of
 * the type they actually want (i.e. `string` instead of `ReadonlyURLSearchParams` or `NextJSSearchParams`).
 *
 * Welcome to React, `use`, and `cache`! `cache` does not support generating cache keys (like `memo` does),
 * and `use` only works with promises that are cached, and does not work if they're recreated on the client
 * (even if they're only recreated just once on page load). This means that if we were to pass in the actual
 * `ReadonlyURLSearchParams` or `NextJSSearchParams` types, React's `cache` couldn't cache the results of these
 * functions (because the refs wouldn't be stable between server and client), causing the error with `use`.
 *
 * Thank you, React. Needed that.
 */

export interface NameData {
	isReal: boolean;
	nickname: string;
	firstName: string;
	lastName?: string;
}

const NICKNAME = 'BellCube';

export const isRealNameValid = cache(async function isRealNameValid(realNameString: string): Promise<boolean> { // so people don't just use my portfolio site with their own name
	const targetHash = process.env.NEXT_PUBLIC_REAL_NAME_HASH;
	const targetHashSalt = process.env.NEXT_PUBLIC_REAL_NAME_HASH_SALT; // because I guess we're stopping rainbow table attacks for my real name now

	if (!targetHashSalt) throw new Error('Missing NEXT_PUBLIC_REAL_NAME_HASH_SALT environment variable!');

	const hashBuffer = new TextEncoder().encode(realNameString + targetHashSalt);
	const hashArray = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', hashBuffer)));
	const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

	if (!targetHash) {
		Log.warnOnce(`Missing NEXT_PUBLIC_REAL_NAME_HASH environment variable! All realName query strings will be accepted. Computed hash for "${realNameString}" with salt "${targetHashSalt}" is "${hashHex}"`);
		return true;
	}

	const isValid = hashHex === targetHash;
	if (!isValid) Log.warn(`Invalid realName query string hash: expected "${targetHash}" but got "${hashHex}" (for input "${realNameString}" with salt "${targetHashSalt}")`);
	return isValid;
});

export const DUMMY_NAME_DATA = {
	isReal: false,
	nickname: NICKNAME,
	firstName: 'Zack',
} as const satisfies NameData;

export const nameDataFromSearchParamsRecord = cache(async function nameDataFromSearchParamsRecord(searchParamsJSON: string) {
	const searchParams = JSON.parse(searchParamsJSON) as NextJSSearchParams;

	let realNameDataRaw = undefined;
	if ('realName' in searchParams && typeof searchParams.realName === 'string') realNameDataRaw = searchParams.realName;

	const [firstName, lastName] = realNameDataRaw?.split(';') || [];

	if (!firstName || !lastName || !(await isRealNameValid(realNameDataRaw!)))
		return DUMMY_NAME_DATA;

	return {
		isReal: true,
		nickname: NICKNAME,
		firstName,
		lastName,
	} as const satisfies NameData;
});

export const nameDataFromSearchParams = cache(async function nameDataFromSearchParams(searchParamsString: string) {
	const searchParams = new URLSearchParams(searchParamsString);

	const realNameData = searchParams.get('realName');

	const [firstName, lastName] = realNameData?.split(';') || [];

	if (!firstName || !lastName || !(await isRealNameValid(realNameData!)))
		return DUMMY_NAME_DATA;

	return {
		isReal: true,
		nickname: NICKNAME,
		firstName,
		lastName,
	} as const satisfies NameData;
});

export type DesiredNameFormat = 'first last' | 'nickname' | 'first "nickname" last';
export type FallbackNameFormat = 'nickname' | 'first';

export function formatName(nameData: NameData, desiredFormat: DesiredNameFormat, fallbackFormat: FallbackNameFormat) {
	let fallback;
	switch (fallbackFormat) {
		case 'nickname':
			fallback = nameData.nickname;
			break;
		case 'first':
			fallback = nameData.firstName;
			break;
	}

	switch (desiredFormat) {
		case 'first last':
			if (nameData.firstName && nameData.lastName)
				return `${nameData.firstName satisfies string} ${nameData.lastName satisfies string}`;

			return fallback;
		case 'nickname':
			return nameData.nickname;
		case 'first "nickname" last':
			if (nameData.firstName && nameData.nickname && nameData.lastName)
				return `${nameData.firstName satisfies string} "${nameData.nickname satisfies string}" ${nameData.lastName satisfies string}`;

			return fallback;
		default:
			return fallback;
	}
}

export const getFormattedNameFromSearchParams =  cache(async function getFormattedNameFromSearchParams(searchParamsString: string, desiredFormat: DesiredNameFormat, fallbackFormat: FallbackNameFormat) {
	const nameData = await nameDataFromSearchParams(searchParamsString);
	return formatName(nameData, desiredFormat, fallbackFormat);
});

export const getFormattedNameFromSearchParamsRecord =  cache(async function getFormattedNameFromSearchParamsRecord(searchParamsJSON: string, desiredFormat: DesiredNameFormat, fallbackFormat: FallbackNameFormat) {
	const nameData = await nameDataFromSearchParamsRecord(searchParamsJSON);
	return formatName(nameData, desiredFormat, fallbackFormat);
});
